import { ISearchCondition, ISearchSettings, SearchCollectionType } from "@gtmhub/shared/services/searchService";
import { MISSING_SESSION_ID_STATE_PARAM } from "@gtmhub/goals/controllers/all";
import { ListTypes } from "@gtmhub/lists";
import { GlobalSearchFacetType, GlobalSearchItem, GlobalSearchItemReturnState, GlobalSearchItemEnriched } from "..";

export const isEqualTo = <T>(compared: T, ...args: T[]): boolean => {
  return args.some((arg) => arg === compared);
};

export const searchCollectionsMap: Record<string, SearchCollectionType[]> = {
  goals: ["goals"],
  okrs: ["goals", "metrics"],
  sessions: ["sessions"],
  tasks: ["tasks"],
  organization: ["users", "teams"],
  employees: ["users"],
  teams: ["teams"],
  lists: ["lists"],
  insightsAndInsightboards: ["insights", "dashboards"],
  insights: ["insights"],
  insightboards: ["dashboards"],
  kpis: ["kpis"],
  automations: ["automations"],
  whiteboards: ["whiteboards"],
  roles: ["roles"],
};

export const searchFieldsMap = {
  goals: ["name", "description", "assignees.name"],
  goalsCompound: ["name", "description", "assignees.name"],
  metrics: ["name", "description", "assignees.name"],
  metricsCompound: ["name", "description", "assignees.name"],
  tasks: ["name", "description", "assignee.name"],
  sessions: ["title"],
  users: ["firstName", "lastName", "fullName", "auth0Cache.email", "teams.name"],
  usersCompound: ["fullName", "auth0Cache.email"],
  teams: ["name", "description"],
  teamsCompound: ["name"],
  insights: ["title", "description", "editor.name"],
  insightsCompound: ["title"],
  dashboards: ["name", "tag"],
  dashboardsCompound: ["name"],
  automations: ["name", "description"],
  kpis: ["name", "description", "assignee.name"],
  lists: ["title"],
  whiteboards: ["name"],
  roles: ["name"],
};

export const responseFieldsMap = {
  goals: ["name", "description", "ownerIds", "sessionId"],
  goalsCompound: ["name", "description", "ownerIds", "sessionId"],
  metrics: ["name", "description", "goalId", "ownerIds", "session._id"],
  metricsCompound: ["name", "description", "goalId", "ownerIds", "session._id"],
  tasks: ["name", "description", "ownerId"],
  sessions: ["title"],
  users: ["fullName", "picture", "auth0Cache.picture", "auth0Cache.email", "teams._id", "teams.name", "isActive"],
  usersCompound: ["fullName", "picture", "auth0Cache.picture", "auth0Cache.email", "teams._id", "teams.name", "isActive"],
  teams: ["name", "description", "picture", "avatar", "color"],
  teamsCompound: ["name", "description", "picture", "avatar", "color"],
  insights: ["title", "description", "name"],
  insightsCompound: ["title", "description", "name"],
  dashboards: ["name"],
  dashboardsCompound: ["name"],
  automations: ["name", "description", "ownerId"],
  kpis: ["name", "description", "ownerId", "ownerIds"],
  lists: ["title", "listType", "ownerId"],
  whiteboards: ["name"],
  roles: ["name"],
};

const toEnrichedGlobalSearchOKR = (
  item: GlobalSearchItem<"goals"> | GlobalSearchItem<"metrics">
): GlobalSearchItemEnriched<"goals"> | GlobalSearchItemEnriched<"metrics"> => {
  if (item.collectionName === "goals") {
    return {
      id: item.id,
      score: item.score,
      collectionName: item.collectionName,
      nameUI: item.fields.name,
      description: item.fields.description,
      assigneeIds: item.fields.ownerIds || [],
      sessionId: item.fields.sessionId,
      icon: "goal",
      isRecent: false,
      lastVisited: "",
    };
  }

  return {
    id: item.id,
    score: item.score,
    collectionName: item.collectionName,
    nameUI: item.fields.name,
    description: item.fields.description,
    assigneeIds: item.fields.ownerIds || [],
    sessionId: item.fields.session._id,
    goalId: item.fields.goalId,
    icon: "metric",
    isRecent: false,
    lastVisited: "",
  };
};

const toEnrichedGlobalSearchSession = (item: GlobalSearchItem<"sessions">): GlobalSearchItemEnriched<"sessions"> => ({
  id: item.id,
  score: item.score,
  collectionName: item.collectionName,
  nameUI: item.fields.title,
  icon: "session",
  isRecent: false,
  lastVisited: "",
});

const toEnrichedGlobalSearchTask = (item: GlobalSearchItem<"tasks">): GlobalSearchItemEnriched<"tasks"> => ({
  id: item.id,
  score: item.score,
  collectionName: item.collectionName,
  nameUI: item.fields.name,
  description: item.fields.description,
  assigneeIds: item.fields.ownerId ? [item.fields.ownerId] : [],
  icon: "task",
  isRecent: false,
  lastVisited: "",
});

const toEnrichedGlobalSearchList = (item: GlobalSearchItem<"lists">): GlobalSearchItemEnriched<"lists"> => {
  let icon: "goal" | "metric" | "task" | "employee" | "team";
  let listType: ListTypes;
  switch (item.fields.listType) {
    case 1:
      icon = "goal";
      listType = ListTypes.Objective;
      break;
    case 2:
      icon = "metric";
      listType = ListTypes["Key Result"];
      break;
    case 3:
      icon = "task";
      listType = ListTypes.Task;
      break;
    case 4:
      icon = "employee";
      listType = ListTypes.Employee;
      break;
    case 5:
      icon = "team";
      listType = ListTypes.Team;
      break;
  }

  return {
    id: item.id,
    score: item.score,
    collectionName: item.collectionName,
    nameUI: item.fields.title,
    assigneeIds: item.fields.ownerId ? [item.fields.ownerId] : [],
    type: listType,
    icon,
    isRecent: false,
    lastVisited: "",
  };
};

const toEnrichedGlobalSearchAutomation = (item: GlobalSearchItem<"automations">): GlobalSearchItemEnriched<"automations"> => ({
  id: item.id,
  score: item.score,
  collectionName: item.collectionName,
  nameUI: item.fields.name,
  description: item.fields.description,
  assigneeIds: item.fields.ownerId ? [item.fields.ownerId] : [],
  icon: "automation",
  isRecent: false,
  lastVisited: "",
});

const toEnrichedGlobalSearchTeam = (item: GlobalSearchItem<"teams">): GlobalSearchItemEnriched<"teams"> => ({
  id: item.id,
  score: item.score,
  collectionName: item.collectionName,
  nameUI: item.fields.name,
  description: item.fields.description,
  picture: item.fields.picture,
  avatar: item.fields.avatar,
  color: item.fields.color,
  shouldDisplayTeamAvatar: true,
  isRecent: false,
  lastVisited: "",
});

const toEnrichedGlobalSearchEmployee = (item: GlobalSearchItem<"users">): GlobalSearchItemEnriched<"users"> => ({
  id: item.id,
  score: item.score,
  collectionName: item.collectionName,
  fullName: item.fields.fullName,
  email: item.fields.auth0Cache.email,
  picture: item.fields.picture || item.fields.auth0Cache.picture,
  teamIds: (item.fields.teams || []).map((team) => team.id),
  isActive: item.fields.isActive,
  shouldDisplayAvatar: true,
  shouldDisplayTeams: true,
  isRecent: false,
  lastVisited: "",
});

const toEnrichedGlobalSearchOrganization = (
  item: GlobalSearchItem<"users"> | GlobalSearchItem<"teams">
): GlobalSearchItemEnriched<"users"> | GlobalSearchItemEnriched<"teams"> => {
  if (item.collectionName === "users") {
    return toEnrichedGlobalSearchEmployee(item);
  }

  return toEnrichedGlobalSearchTeam(item);
};

const toEnrichedGlobalSearchKPI = (item: GlobalSearchItem<"kpis">): GlobalSearchItemEnriched<"kpis"> => ({
  id: item.id,
  score: item.score,
  collectionName: item.collectionName,
  nameUI: item.fields.name,
  description: item.fields.description,
  assigneeIds: item.fields.ownerIds ? item.fields.ownerIds : item.fields.ownerId ? [item.fields.ownerId] : [],
  icon: "kpi",
  isRecent: false,
  lastVisited: "",
});

const toEnrichedGlobalSearchInsight = (item: GlobalSearchItem<"insights">): GlobalSearchItemEnriched<"insights"> => ({
  id: item.id,
  score: item.score,
  collectionName: item.collectionName,
  icon: "insight",
  nameUI: item.fields.title,
  name: item.fields.name,
  description: item.fields.description,
  isRecent: false,
  lastVisited: "",
});

const toEnrichedGlobalSearchInsightboard = (item: GlobalSearchItem<"dashboards">): GlobalSearchItemEnriched<"dashboards"> => ({
  id: item.id,
  score: item.score,
  collectionName: item.collectionName,
  icon: "insightboard",
  nameUI: item.fields.name,
  isRecent: false,
  lastVisited: "",
});

const toEnrichedGlobalSearchInsightOrInsightboard = (
  item: GlobalSearchItem<"insights"> | GlobalSearchItem<"dashboards">
): GlobalSearchItemEnriched<"insights"> | GlobalSearchItemEnriched<"dashboards"> => {
  if (item.collectionName === "insights") {
    return toEnrichedGlobalSearchInsight(item);
  }

  return toEnrichedGlobalSearchInsightboard(item);
};

const toEnrichedGlobalSearchWhiteboard = (item: GlobalSearchItem<"whiteboards">): GlobalSearchItemEnriched<"whiteboards"> => ({
  id: item.id,
  score: item.score,
  collectionName: item.collectionName,
  nameUI: item.fields.name,
  icon: "whiteboard",
  isRecent: false,
  lastVisited: "",
});

const toEnrichedGlobalSearchRole = (item: GlobalSearchItem<"roles">): GlobalSearchItemEnriched<"roles"> => ({
  id: item.id,
  score: item.score,
  collectionName: item.collectionName,
  nameUI: item.fields.name,
  icon: "role",
  isRecent: false,
  lastVisited: "",
});

export const searchCollectionFormatterMap: Record<string, (items: GlobalSearchItem<SearchCollectionType>[]) => GlobalSearchItemEnriched<SearchCollectionType>[]> = {
  okrs: (items: (GlobalSearchItem<"goals"> | GlobalSearchItem<"metrics">)[]): (GlobalSearchItemEnriched<"goals"> | GlobalSearchItemEnriched<"metrics">)[] => {
    return items.map((item) => toEnrichedGlobalSearchOKR(item));
  },
  sessions: (items: GlobalSearchItem<"sessions">[]): GlobalSearchItemEnriched<"sessions">[] => {
    return items.map((item) => toEnrichedGlobalSearchSession(item));
  },
  tasks: (items: GlobalSearchItem<"tasks">[]): GlobalSearchItemEnriched<"tasks">[] => {
    return items.map((item) => toEnrichedGlobalSearchTask(item));
  },
  lists: (items: GlobalSearchItem<"lists">[]): GlobalSearchItemEnriched<"lists">[] => {
    return items.map((item) => toEnrichedGlobalSearchList(item));
  },
  automations: (items: GlobalSearchItem<"automations">[]): GlobalSearchItemEnriched<"automations">[] => {
    return items.map((item) => toEnrichedGlobalSearchAutomation(item));
  },
  teams: (items: GlobalSearchItem<"teams">[]): GlobalSearchItemEnriched<"teams">[] => {
    return items.map((item) => toEnrichedGlobalSearchTeam(item));
  },
  employees: (items: GlobalSearchItem<"users">[]): GlobalSearchItemEnriched<"users">[] => {
    return items.map((item) => toEnrichedGlobalSearchEmployee(item));
  },
  organization: (items: (GlobalSearchItem<"users"> | GlobalSearchItem<"teams">)[]): (GlobalSearchItemEnriched<"users"> | GlobalSearchItemEnriched<"teams">)[] => {
    return items.map((item) => toEnrichedGlobalSearchOrganization(item));
  },
  kpis: (items: GlobalSearchItem<"kpis">[]): GlobalSearchItemEnriched<"kpis">[] => {
    return items.map((item) => toEnrichedGlobalSearchKPI(item));
  },
  insights: (items: GlobalSearchItem<"insights">[]): GlobalSearchItemEnriched<"insights">[] => {
    return items.map((item) => toEnrichedGlobalSearchInsight(item));
  },
  insightboards: (items: GlobalSearchItem<"dashboards">[]): GlobalSearchItemEnriched<"dashboards">[] => {
    return items.map((item) => toEnrichedGlobalSearchInsightboard(item));
  },
  insightsAndInsightboards: (
    items: (GlobalSearchItem<"insights"> | GlobalSearchItem<"dashboards">)[]
  ): (GlobalSearchItemEnriched<"insights"> | GlobalSearchItemEnriched<"dashboards">)[] => {
    return items.map((item) => toEnrichedGlobalSearchInsightOrInsightboard(item));
  },
  whiteboards: (items: GlobalSearchItem<"whiteboards">[]): GlobalSearchItemEnriched<"whiteboards">[] => {
    return items.map((item) => toEnrichedGlobalSearchWhiteboard(item));
  },
  roles: (items: GlobalSearchItem<"roles">[]): GlobalSearchItemEnriched<"roles">[] => {
    return items.map((item) => toEnrichedGlobalSearchRole(item));
  },
};

export const searchItemsToEnrichedGlobalSearchItemCollection = (
  collection: string,
  items: GlobalSearchItem<SearchCollectionType>[]
): GlobalSearchItemEnriched<SearchCollectionType>[] => {
  return searchCollectionFormatterMap[collection](items);
};

// RETURN STATES
export const returnStatesMap: Record<
  Exclude<GlobalSearchFacetType, "insights" | "everywhere"> | "roles",
  (item: GlobalSearchItemEnriched<SearchCollectionType>) => GlobalSearchItemReturnState
> = {
  sessions: (item: GlobalSearchItemEnriched<"sessions">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.goals.all",
      stateParams: { planningSessionId: item.id, page: 1 },
    };
  },
  okrs: (item: GlobalSearchItemEnriched<"goals"> | GlobalSearchItemEnriched<"metrics">): GlobalSearchItemReturnState => {
    const sessionId = item.sessionId || MISSING_SESSION_ID_STATE_PARAM;

    if (item.collectionName === "metrics") {
      return {
        state: "gtmhub.goals.all._goalSidebar.goal._metric.metric",
        stateParams: { id: item.goalId, planningSessionId: sessionId, metricId: item.id },
      };
    } else {
      return {
        state: "gtmhub.goals.all._goalSidebar.goal",
        stateParams: { id: item.id, planningSessionId: sessionId },
      };
    }
  },
  tasks: (item: GlobalSearchItemEnriched<"tasks">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.home._task.task",
      stateParams: { taskId: item.id },
    };
  },
  lists: (item: GlobalSearchItemEnriched<"lists">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.list",
      stateParams: { listId: item.id, isNewList: false },
    };
  },
  automations: (item: GlobalSearchItemEnriched<"automations">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.automation.rules",
      stateParams: { automationId: item.id },
    };
  },
  teams: (item: GlobalSearchItemEnriched<"teams">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.teams.team",
      stateParams: { teamId: item.id },
    };
  },
  employees: (item: GlobalSearchItemEnriched<"users">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.employees.employee",
      stateParams: { employeeId: item.id },
    };
  },
  organization: (item: GlobalSearchItemEnriched<"users"> | GlobalSearchItemEnriched<"teams">): GlobalSearchItemReturnState => {
    if (item.collectionName === "teams") {
      return returnStatesMap.teams(item);
    } else {
      return returnStatesMap.employees(item);
    }
  },
  kpis: (item: GlobalSearchItemEnriched<"kpis">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.kpis._singleKpi.form",
      stateParams: { kpiId: item.id },
    };
  },
  insightboards: (item: GlobalSearchItemEnriched<"dashboards">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.insightboard",
      stateParams: { dashboardId: item.id },
    };
  },
  insightsAndInsightboards: (item: GlobalSearchItemEnriched<"dashboards">): GlobalSearchItemReturnState => {
    return returnStatesMap.insightboards(item);
  },
  whiteboards: (item: GlobalSearchItemEnriched<"whiteboards">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.whiteboard",
      stateParams: { whiteboardId: item.id },
    };
  },
  roles: (item: GlobalSearchItemEnriched<"roles">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.roles",
      stateParams: { roleId: item.id },
    };
  },
};

export const getItemReturnState = (facet: string, item: GlobalSearchItemEnriched<SearchCollectionType>): GlobalSearchItemReturnState => {
  return returnStatesMap[facet](item);
};

export const buildSearchConditions = (collection: string, settings: ISearchSettings): ISearchCondition[] => {
  const conditions = [];
  if (collection === "users" && settings.activeUsersOnly) {
    conditions.push({
      fieldName: "isActive",
      fieldCondition: {
        operator: "isNot",
        value: "false",
      },
    });
  }
  return conditions;
};

export const onSearchItemHover = ($event: JQueryEventObject, hovered: boolean): void => {
  const selectedElementClassName = "selected-item";
  const listElementClassName = "item";

  const selectedEl = document.querySelector(`.${selectedElementClassName}`);
  if (selectedEl && selectedEl.classList.contains(listElementClassName)) {
    selectedEl.classList.remove(selectedElementClassName);
  }

  if (hovered) {
    $event.target.classList.add(selectedElementClassName);
  } else {
    $event.target.classList.remove(selectedElementClassName);
  }
};

const canMoveToTargetElement = (params: { potentialNextElementInSameSection: HTMLElement; listElementClassName: string }): boolean =>
  params.potentialNextElementInSameSection && params.potentialNextElementInSameSection.classList.contains(params.listElementClassName);

const getTargetElementOnSectionEndReached = (params: {
  direction: "up" | "down";
  itemsPerSection: number;
  currentItemId: string[];
  listElementClassName: string;
}): HTMLElement => {
  const shouldMoveToNextSection = params.direction === "down";
  let targetElement: HTMLElement;

  if (shouldMoveToNextSection) {
    const nextSectionIndex = +params.currentItemId[0] + 1;
    const firstElementInNextSectionId = `${nextSectionIndex}-0`;
    targetElement = document.getElementById(firstElementInNextSectionId);
  } else {
    for (let itemIdx = params.itemsPerSection + 1; 0 <= itemIdx; itemIdx--) {
      const previousSectionIndex = +params.currentItemId[0] - 1;
      const lastElementInPreviousSectionId = `${previousSectionIndex}-${itemIdx}`;

      targetElement = document.getElementById(lastElementInPreviousSectionId);
      if (canMoveToTargetElement({ potentialNextElementInSameSection: targetElement, listElementClassName: params.listElementClassName })) {
        break;
      }
    }
  }

  return targetElement;
};

const getTargetElementOnSectionListEndReached = (params: {
  direction: "up" | "down";
  itemsPerSection: number;
  sectionsShown: number;
  listElementClassName: string;
}): HTMLElement => {
  const shouldMoveToFirstElementInFirstSection = params.direction === "down";
  let targetElement: HTMLElement;

  if (shouldMoveToFirstElementInFirstSection) {
    const firstListElementInFirstSectionId = "0-0";
    targetElement = document.getElementById(firstListElementInFirstSectionId);
  } else {
    // ==> move to the last element of the last section
    for (let idxFacet = params.sectionsShown - 1; 0 <= idxFacet; idxFacet--) {
      for (let idxItem = params.itemsPerSection + 1; 0 <= idxItem; idxItem--) {
        targetElement = document.getElementById(`${idxFacet}-${idxItem}`);
        if (canMoveToTargetElement({ potentialNextElementInSameSection: targetElement, listElementClassName: params.listElementClassName })) {
          break;
        }
      }
      if (canMoveToTargetElement({ potentialNextElementInSameSection: targetElement, listElementClassName: params.listElementClassName })) {
        break;
      }
    }
  }

  return targetElement;
};

const handleUpDownArrowPressOnExistingSelectedElement = (params: {
  $event: Event;
  direction: "up" | "down";
  itemsPerSection: number;
  sectionsShown: number;
  selectedElementClassName: string;
  listElementClassName: string;
  selectedEl: HTMLElement;
}): void => {
  const currentItemId = params.selectedEl.id.split("-");
  const previousElementIndex = +currentItemId[1] - 1;
  const previousElementInSameSectionId = `${currentItemId[0]}-${previousElementIndex}`;
  const nextElementIndex = +currentItemId[1] + 1;
  const nextElementInSameSectionId = `${currentItemId[0]}-${nextElementIndex}`;

  const targetElementId = params.direction === "down" ? nextElementInSameSectionId : previousElementInSameSectionId;
  let targetElement = document.getElementById(targetElementId);

  if (canMoveToTargetElement({ potentialNextElementInSameSection: targetElement, listElementClassName: params.listElementClassName })) {
    params.selectedEl.classList.remove(params.selectedElementClassName);
    targetElement.classList.add(params.selectedElementClassName);
  } else {
    // End of section
    targetElement = getTargetElementOnSectionEndReached({
      direction: params.direction,
      itemsPerSection: params.itemsPerSection,
      currentItemId,
      listElementClassName: params.listElementClassName,
    });

    if (canMoveToTargetElement({ potentialNextElementInSameSection: targetElement, listElementClassName: params.listElementClassName })) {
      params.selectedEl.classList.remove(params.selectedElementClassName);
      targetElement.classList.add(params.selectedElementClassName);
    } else {
      // End of sections list
      params.selectedEl.classList.remove(params.selectedElementClassName);

      targetElement = getTargetElementOnSectionListEndReached({
        direction: params.direction,
        itemsPerSection: params.itemsPerSection,
        sectionsShown: params.sectionsShown,
        listElementClassName: params.listElementClassName,
      });

      targetElement.classList.add(params.selectedElementClassName);
    }
  }
};

export const selectElementOnUpDownArrowPress = (params: {
  $event: Event;
  direction: "up" | "down";
  itemsPerSection: number;
  sectionsShown: number;
  selectedElementClassName: string;
  listElementClassName: string;
}): void => {
  params.$event.preventDefault();

  let selectedEl = document.querySelector<HTMLElement>(`.${params.selectedElementClassName}`);

  const isAnyListElementSelected = selectedEl && selectedEl.classList.contains(params.listElementClassName);
  if (!isAnyListElementSelected) {
    const firstListElementId = "0-0";
    selectedEl = document.getElementById(firstListElementId);
    selectedEl.classList.add(params.selectedElementClassName);
  } else {
    handleUpDownArrowPressOnExistingSelectedElement({
      ...params,
      selectedEl,
    });
  }
};
